// [목적] 디자인 토큰을 MUI v5 Theme 으로 매핑하는 createMuiTheme(mode) 함수 (Spec ADR-4)
// [이유] CLAUDE.md §6-3 디자인 토큰 강제 — apps/web/admin 이 본 함수를 통해서만 MUI 테마 생성
// [사이드 이팩트] palette/typography/shape/spacing/transitions 가 모두 토큰 파생. MUI sx 시스템과 정합
// [수정 시 고려사항] Dark 팔레트는 토큰만 정의됨. 실제 다크모드 적용은 후속 Spec(theme-darkmode)에서. mode='dark' 호출 시도 가능하지만 부분 매핑임
//                   [2026-09-17 디자인 리뷰] components 에 전역 키보드 포커스 링 추가(CLAUDE §6-4 :focus-visible) —
//                   ButtonBase 계열(.Mui-focusVisible)과 CssBaseline 의 a·[role=button]:focus-visible. 컴포넌트 sx 의
//                   개별 focus-visible 스타일이 있으면 그쪽이 우선(더 구체적). 링 색은 brand 토큰.

import { createTheme, type Theme } from '@mui/material/styles';

import type { ColorMode } from './tokens/color.js';
import { tokens } from './tokens/index.js';

export function createMuiTheme(mode: ColorMode = 'light'): Theme {
  const c = tokens.color;
  const t = tokens.typography;
  return createTheme({
    palette: {
      mode,
      primary: { main: c.brand[500][mode] },
      success: { main: c.accent[500][mode] },
      warning: { main: c.warm[500][mode] },
      error: { main: c.danger[500][mode] },
      // 편지지 톤(paper)을 기본 배경으로 매핑. surface 토큰은 쿨톤 다이얼로그 등을 위해 보존.
      background: { default: c.paper[1][mode], paper: c.paper[0][mode] },
      text: { primary: c.ink.high[mode], secondary: c.ink.mid[mode] },
      divider: c.line.base[mode],
    },
    typography: {
      fontFamily: t.fontFamily.sans,
      h1: {
        fontSize: t.scale['display.xl'].size,
        lineHeight: `${t.scale['display.xl'].line}px`,
        fontWeight: t.scale['display.xl'].weight,
      },
      h2: {
        fontSize: t.scale['display.l'].size,
        lineHeight: `${t.scale['display.l'].line}px`,
        fontWeight: t.scale['display.l'].weight,
      },
      h3: {
        fontSize: t.scale['heading.m'].size,
        lineHeight: `${t.scale['heading.m'].line}px`,
        fontWeight: t.scale['heading.m'].weight,
      },
      h4: {
        fontSize: t.scale['heading.s'].size,
        lineHeight: `${t.scale['heading.s'].line}px`,
        fontWeight: t.scale['heading.s'].weight,
      },
      body1: {
        fontSize: t.scale['body.m'].size,
        lineHeight: `${t.scale['body.m'].line}px`,
        fontWeight: t.scale['body.m'].weight,
      },
      body2: {
        fontSize: t.scale['body.s'].size,
        lineHeight: `${t.scale['body.s'].line}px`,
        fontWeight: t.scale['body.s'].weight,
      },
    },
    components: {
      MuiButtonBase: {
        styleOverrides: {
          root: {
            // [목적] 버튼 계열의 본문 폰트 통일
            // [이유] <button> 은 UA 기본 스타일에서 font-family 를 상속하지 않아(브라우저 기본 = Arial 등)
            //        ButtonBase 로 만든 모든 버튼이 Pretendard 가 아닌 시스템 폰트로 렌더됐다.
            //        한글 자소 폭·굵기가 본문과 달라 액션 바·탭에서 위계가 무너져 보였다.
            // [사이드 이팩트] 앱 전역 버튼 텍스트가 typography.fontFamily 로 통일된다(의도된 변경).
            // [수정 시 고려사항] 모노 표기가 필요한 버튼은 개별 sx 에서 fontFamily 를 덮어쓸 것.
            fontFamily: t.fontFamily.sans,
            '&.Mui-focusVisible': { outline: `2px solid ${c.brand[500][mode]}`, outlineOffset: 2 },
          },
        },
      },
      MuiCssBaseline: {
        styleOverrides: {
          // [목적] 목록 결과 수가 바뀌어 세로 스크롤바가 나타나거나 사라져도 중앙 정렬 화면의 좌우 위치를 유지한다.
          // [이유] 축제 상태 필터처럼 긴 목록과 빈 상태를 전환할 때 뷰포트 유효 폭이 달라져 화면이 흔들렸다.
          // [사이드 이팩트] 스크롤이 없는 짧은 화면도 스크롤바 여백을 안정적으로 예약한다.
          // [수정 시 고려사항] 독립 스크롤 컨테이너는 해당 요소에 별도 scrollbar-gutter 설정이 필요하다.
          html: { scrollbarGutter: 'stable' },
          'a:focus-visible, [role="button"]:focus-visible': {
            outline: `2px solid ${c.brand[500][mode]}`,
            outlineOffset: 2,
          },
        },
      },
    },
    shape: { borderRadius: tokens.radius.m },
    spacing: (factor: number) => `${factor * 4}px`,
    breakpoints: {
      values: { xs: 0, sm: 600, md: 900, lg: 1200, xl: 1536 },
    },
    transitions: {
      duration: {
        shortest: tokens.motion.fast.duration,
        shorter: tokens.motion.fast.duration,
        short: tokens.motion.med.duration,
        standard: tokens.motion.med.duration,
        complex: tokens.motion.slow.duration,
        enteringScreen: tokens.motion.med.duration,
        leavingScreen: tokens.motion.fast.duration,
      },
    },
  });
}

export type { Theme };
